import {
  type ElementType,
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useRef,
  useState,
} from "react";

import {
  Box,
  Column,
  IconBox,
  Row,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import {
  COLUMN_CATEGORY_META,
  type ColumnCategory,
} from "src/formkit/components/mapper-v2/column-categories";
import {
  getNextIndex,
  isListNavigationKey,
} from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  getNavItemKey,
  getNavTabId,
  isSameSelection,
  type NavSelection,
  type OtherValueType,
} from "src/formkit/components/mapper-v2/mapper-selection";
import { useScrollFade } from "src/hooks/use-scroll-fade";

export type MapperNavOtherItem = {
  type: OtherValueType;
  label: string;
  icon: ElementType;
};

type MapperNavProps = {
  idPrefix: string;
  tabPanelId: string;
  categories: ColumnCategory[];
  otherItems: MapperNavOtherItem[];
  selection: NavSelection;
  /** `fromPointer` is false when the tab was activated from the keyboard. */
  onSelect: (selection: NavSelection, fromPointer: boolean) => void;
};

type NavEntry = {
  selection: NavSelection;
  label: string;
  icon: ReactNode;
  count: number | undefined;
};

/**
 * The popover's vertical tab list. Arrow keys, Home, and End move focus
 * without selecting; Enter, Space, or a click selects. Tab leaves the list
 * from any tab and comes back to the selected one.
 */
export function MapperNav({
  idPrefix,
  tabPanelId,
  categories,
  otherItems,
  selection,
  onSelect,
}: MapperNavProps) {
  const scrollFade = useScrollFade();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // While focus is in the list, the focused tab is its only tab stop.
  const [focusedKey, setFocusedKey] = useState<string>();

  const columnEntries = categories.map((category): NavEntry => {
    const meta = COLUMN_CATEGORY_META[category.kind];
    const Icon = meta.icon;
    return {
      selection: { kind: "column", categoryId: category.id },
      label: category.label,
      icon: (
        <IconBox
          color={meta.color}
          icon={<Icon />}
          size="sm"
          variant={meta.variant}
        />
      ),
      // Typed-name categories start empty and show no count.
      count: category.options.length || undefined,
    };
  });
  const otherEntries = otherItems.map(
    (item): NavEntry => ({
      selection: { kind: "other", type: item.type },
      label: item.label,
      icon: <Box as={item.icon} boxSize={5} color="icon.tertiary" />,
      count: undefined,
    }),
  );

  const entries = [...columnEntries, ...otherEntries];
  // The focused tab, else the selected one, else the first, so a selection that matches no tab can't leave the list unreachable.
  const tabStopEntry =
    entries.find((entry) => getNavItemKey(entry.selection) === focusedKey) ??
    entries.find((entry) => isSameSelection(entry.selection, selection)) ??
    entries[0];

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (!isListNavigationKey(event.key)) return;
    event.preventDefault();
    tabRefs.current[getNextIndex(index, event.key, entries.length)]?.focus();
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node && event.currentTarget.contains(next))) {
      setFocusedKey(undefined);
    }
  };

  const renderEntry = (entry: NavEntry, index: number) => {
    const key = getNavItemKey(entry.selection);
    const isSelected = isSameSelection(entry.selection, selection);
    return (
      <MapperNavItem
        key={key}
        ref={(element) => {
          tabRefs.current[index] = element;
        }}
        count={entry.count}
        icon={entry.icon}
        id={getNavTabId(idPrefix, entry.selection)}
        isSelected={isSelected}
        isTabStop={entry === tabStopEntry}
        label={entry.label}
        tabPanelId={tabPanelId}
        // A keyboard-activated button reports a click with `detail` 0.
        onClick={(event) => onSelect(entry.selection, event.detail > 0)}
        onFocus={() => setFocusedKey(key)}
        onKeyDown={(event) => handleKeyDown(event, index)}
      />
    );
  };

  return (
    <Column
      ref={scrollFade.ref}
      aria-label="Mapping type"
      aria-orientation="vertical"
      bg="base.background"
      borderColor="base.divider"
      borderRightStyle="solid"
      borderRightWidth="hairline"
      flexShrink={0}
      gap={6}
      overflowY="auto"
      px={2}
      py={4}
      role="tablist"
      sx={scrollFade.sx}
      width="2xs"
      onBlur={handleBlur}
    >
      {columnEntries.length > 0 && (
        <MapperNavSection heading="Columns">
          {columnEntries.map((entry, index) => renderEntry(entry, index))}
        </MapperNavSection>
      )}
      {otherEntries.length > 0 && (
        <MapperNavSection heading="Other values">
          {otherEntries.map((entry, index) =>
            renderEntry(entry, columnEntries.length + index),
          )}
        </MapperNavSection>
      )}
    </Column>
  );
}

function MapperNavSection({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <Column gap={2}>
      <Text color="text.tertiary" fontWeight="medium" ml={2} size="sm">
        {heading}
      </Text>
      <Column>{children}</Column>
    </Column>
  );
}

type MapperNavItemProps = {
  id: string;
  tabPanelId: string;
  isSelected: boolean;
  isTabStop: boolean;
  label: string;
  icon: ReactNode;
  count: number | undefined;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

const MapperNavItem = forwardRef<HTMLButtonElement, MapperNavItemProps>(
  function MapperNavItem(
    {
      id,
      tabPanelId,
      isSelected,
      isTabStop,
      label,
      icon,
      count,
      onClick,
      onFocus,
      onKeyDown,
    },
    ref,
  ) {
    return (
      <Row
        ref={ref}
        _focusVisible={{ boxShadow: "outline" }}
        _hover={isSelected ? undefined : { bg: "base.backgroundHover" }}
        alignItems="center"
        aria-controls={tabPanelId}
        aria-selected={isSelected}
        as="button"
        bg={isSelected ? "white" : undefined}
        borderColor={isSelected ? "base.border" : "base.borderTransparent"}
        borderRadius="lg"
        borderStyle="solid"
        borderWidth="hairline"
        boxShadow={isSelected ? "buttonShadowLight" : undefined}
        cursor="pointer"
        flexShrink={0}
        gap={2}
        height={9}
        id={id}
        justifyContent="space-between"
        outline="none"
        p={2}
        role="tab"
        tabIndex={isTabStop ? 0 : -1}
        textAlign="left"
        type="button"
        width="100%"
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      >
        <Row alignItems="center" gap={2} minWidth={0}>
          <Row flexShrink={0}>{icon}</Row>
          <TextWithTooltip color="text.secondary" fontWeight="medium">
            {label}
          </TextWithTooltip>
        </Row>
        {count !== undefined && (
          <Box flexShrink={0} minWidth={5} textAlign="center">
            <Text color="text.tertiary" size="sm">
              {count}
            </Text>
          </Box>
        )}
      </Row>
    );
  },
);
